
import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Node, Edge } from '@xyflow/react';
import { 
  convertFlowNodeToReactFlow,
  convertFlowEdgeToReactFlow,
  FlowNode,
  FlowEdge
} from '@/services/supabase';

interface CollaborationHookProps {
  spaceId: string;
  nodes: Node[];
  edges: Edge[];
  setNodes: (nodes: Node[] | ((prev: Node[]) => Node[])) => void;
  setEdges: (edges: Edge[] | ((prev: Edge[]) => Edge[])) => void;
  onSaveNote: (nodeId: string, content: string) => void;
  onDeleteNote: (nodeId: string) => void;
  onEditNote: (nodeId: string, isEditing: boolean) => void;
  onHighlightNote: (nodeId: string) => void;
}

export const useCollaboration = ({
  spaceId,
  nodes,
  edges,
  setNodes,
  setEdges,
  onSaveNote,
  onDeleteNote,
  onEditNote,
  onHighlightNote
}: CollaborationHookProps) => {
  const { user } = useAuth();

  // Handle real-time node updates
  const handleNodeUpdate = useCallback((payload: any) => {
    console.log('Real-time node update:', payload);
    
    if (payload.eventType === 'INSERT') {
      const newNode = convertFlowNodeToReactFlow(payload.new as FlowNode);
      
      // Add callback functions for sticky notes
      if (payload.new.node_type === 'stickyNote') {
        newNode.data = {
          ...newNode.data,
          onSave: onSaveNote,
          onDelete: onDeleteNote,
          onEdit: onEditNote,
          onHighlight: onHighlightNote,
        };
      }
      
      setNodes(prev => {
        // Check if node already exists to prevent duplicates
        if (prev.find(n => n.id === newNode.id)) {
          return prev;
        }
        return [...prev, newNode];
      });
    } else if (payload.eventType === 'UPDATE') {
      const updatedNode = convertFlowNodeToReactFlow(payload.new as FlowNode);
      
      // Add callback functions for sticky notes
      if (payload.new.node_type === 'stickyNote') {
        updatedNode.data = {
          ...updatedNode.data,
          onSave: onSaveNote,
          onDelete: onDeleteNote,
          onEdit: onEditNote,
          onHighlight: onHighlightNote,
        };
      }
      
      setNodes(prev => prev.map(node => 
        node.id === updatedNode.id ? updatedNode : node
      ));
    } else if (payload.eventType === 'DELETE') {
      setNodes(prev => prev.filter(node => node.id !== payload.old.id));
    }
  }, [onSaveNote, onDeleteNote, onEditNote, onHighlightNote, setNodes]);

  // Handle real-time edge updates
  const handleEdgeUpdate = useCallback((payload: any) => {
    console.log('Real-time edge update:', payload);
    
    if (payload.eventType === 'INSERT') {
      const newEdge = convertFlowEdgeToReactFlow(payload.new as FlowEdge);
      setEdges(prev => {
        // Check if edge already exists to prevent duplicates
        if (prev.find(e => e.id === newEdge.id)) {
          return prev;
        }
        return [...prev, newEdge];
      });
    } else if (payload.eventType === 'UPDATE') {
      const updatedEdge = convertFlowEdgeToReactFlow(payload.new as FlowEdge);
      setEdges(prev => prev.map(edge => 
        edge.id === updatedEdge.id ? updatedEdge : edge
      ));
    } else if (payload.eventType === 'DELETE') {
      setEdges(prev => prev.filter(edge => edge.id !== payload.old.id));
    }
  }, [setEdges]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!user || !spaceId) return;

    console.log('Setting up real-time subscriptions for space:', spaceId);

    // Subscribe to node changes
    const nodeChannel = supabase
      .channel(`nodes-${spaceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'flow_nodes',
          filter: `note_space_id=eq.${spaceId}`
        },
        handleNodeUpdate
      )
      .subscribe();

    // Subscribe to edge changes
    const edgeChannel = supabase
      .channel(`edges-${spaceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'flow_edges',
          filter: `note_space_id=eq.${spaceId}`
        },
        handleEdgeUpdate
      )
      .subscribe();

    return () => {
      console.log('Cleaning up real-time subscriptions');
      supabase.removeChannel(nodeChannel);
      supabase.removeChannel(edgeChannel);
    };
  }, [user, spaceId, handleNodeUpdate, handleEdgeUpdate]);

  return {
    // This hook mainly handles side effects for real-time updates
    // No return values needed for now
  };
};
